var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { renderer } from "../rendering/RendererGlobals";
import { clockService } from "../xr/ClockService";
import { preferencesService } from "../preferences/PreferencesService";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { renderManager } from "../xr/RenderManager";
import { NewModule } from "../module/NewModule";
const SKIP_SECONDS_AFTER_SCENE_VISIBLE = 1;
const MIN_PIXEL_RATIO = 0.05;
// Maximaler Pixel Ratio auf 2 begrenzen – vermeidet exzessiven GPU-Aufwand auf HiDPI-Displays
const MAX_PIXEL_RATIO = Math.min(window.devicePixelRatio, 2);
/**
 * Automatically adjusts the renderer pixel ratio to keep frame times in a usable range while still honoring persisted resolution preferences.
 */
export class AutoPixelRatioModule extends NewModule {
    constructor() {
        super("AutoPixelRatioModule");
        this.deltas = [];
        this.secondsSinceMeasurementStart = 0;
        this.secondsSinceSceneVisible = 0;
        // Pixel Ratio auf 2 deckeln – konsistent mit RendererGlobals
        this.maxPixelRatio = MAX_PIXEL_RATIO;
        this.pixelRatioToTimesDecreased = new Map();
        // Schnellere Anpassungsrate: 0.1 statt 0.05 → halb so viele Zyklen bis zur optimalen Auflösung
        this.changeRate = 0.1;
        // Schwellenwerte erhöht: Anpassung beginnt früher (30 fps statt 25) und hört früher auf (50 statt 40)
        this.lowFpsThreshold = 30;
        this.hightFpsThreshold = 50;
        this.measurementPeriodInSeconds = 1.5;
        /// Private helper methods ///
        this.update = () => {
            if (renderer.xr.isPresenting)
                return; // No pixel ratio change when in VR
            const deltaTime = clockService.getDeltaToLastFrameInSeconds();
            if (this.secondsSinceSceneVisible < SKIP_SECONDS_AFTER_SCENE_VISIBLE) {
                this.secondsSinceSceneVisible += deltaTime;
                return;
            }
            if (document.visibilityState === "hidden")
                return;
            this.deltas.push(deltaTime);
            this.secondsSinceMeasurementStart += deltaTime;
            if (this.secondsSinceMeasurementStart > this.measurementPeriodInSeconds) {
                this.deltas.sort();
                const medianDelta = this.deltas[Math.floor(this.deltas.length / 2)];
                const medianFps = 1.0 / medianDelta;
                const currentPixelRatio = renderer.getPixelRatio();
                const shouldDecrease = currentPixelRatio > MIN_PIXEL_RATIO && medianFps < this.lowFpsThreshold;
                const shouldIncrease = currentPixelRatio < this.maxPixelRatio && medianFps > this.hightFpsThreshold;
                if (shouldDecrease || shouldIncrease) {
                    let newPixelRatio = currentPixelRatio + this.changeRate * (shouldIncrease ? 1 : -1);
                    if (newPixelRatio < MIN_PIXEL_RATIO) {
                        newPixelRatio = MIN_PIXEL_RATIO;
                    }
                    else if (newPixelRatio > this.maxPixelRatio) {
                        newPixelRatio = this.maxPixelRatio;
                    }
                    console.warn(`[auto] auto pixelratio: Median FPS (${medianFps.toFixed()}) was ${shouldIncrease ? `above ${this.hightFpsThreshold}` : `below ${this.lowFpsThreshold}`}. Adjusting pixel ratio to ${newPixelRatio}`);
                    renderer.setPixelRatio(newPixelRatio);
                    // const resolutionFactor = shouldDecrease ? Math.floor(1 / renderer.getPixelRatio()) : Math.ceil(1 / renderer.getPixelRatio());
                    preferencesService.updatePreferences([{ key: "resolutionFactor", value: 1 / renderer.getPixelRatio() }], false);
                    renderManager.render();
                    if (shouldDecrease) {
                        const timesDecreased = (this.pixelRatioToTimesDecreased.get(currentPixelRatio) || 0) + 1;
                        this.pixelRatioToTimesDecreased.set(currentPixelRatio, timesDecreased);
                    }
                }
                this.deltas.length = 0;
                this.secondsSinceMeasurementStart = 0;
            }
        };
    }
    /// Life cycle methods ///
    onInit() {
        const autoResolution = preferencesService.getPreferredValue("autoResolution", "on");
        let resolutionFactor = preferencesService.getNumberPreferredValue("resolutionFactor", 1);
        if (autoResolution === "on" && resolutionFactor) {
            renderer.setPixelRatio(1 / resolutionFactor);
            animationFrame$.onRender$.add(this.update);
        }
        else if (resolutionFactor) {
            if (resolutionFactor === 1)
                renderer.setPixelRatio(MAX_PIXEL_RATIO);
            else
                renderer.setPixelRatio(1 / resolutionFactor);
        }
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "autoResolution" &&
                key !== "resolutionFactor" &&
                key !== "framerateThresholdLow" &&
                key !== "framerateThresholdHigh" &&
                key !== "changeRate" &&
                key !== "measuringTime")
                return false;
            resolutionFactor = preferencesService.getNumberPreferredValue("resolutionFactor", 1);
            if (key === "autoResolution") {
                if (newValue === "off") {
                    if (resolutionFactor === 1) {
                        renderer.setPixelRatio(MAX_PIXEL_RATIO);
                    }
                    else if (resolutionFactor) {
                        const newResolutionFactor = 1 / renderer.getPixelRatio();
                        preferencesService.updatePreferences([{ key: "resolutionFactor", value: newResolutionFactor }]);
                        renderer.setPixelRatio(1 / newResolutionFactor);
                    }
                    // Prevent flickering of 3D View
                    renderManager.render();
                    animationFrame$.onRender$.remove(this.update);
                }
                else {
                    animationFrame$.onRender$.add(this.update);
                }
                return true;
            }
            else if (key === "resolutionFactor") {
                if (resolutionFactor) {
                    renderer.setPixelRatio(1 / resolutionFactor);
                    // Prevent flickering of 3D View
                    renderManager.render();
                }
            }
            else if (key === "framerateThresholdLow") {
                const framerateThresholdLow = preferencesService.getNumberPreferredValue("framerateThresholdLow", 25);
                if (framerateThresholdLow)
                    this.lowFpsThreshold = framerateThresholdLow;
            }
            else if (key === "framerateThresholdHigh") {
                const framerateThresholdHigh = preferencesService.getNumberPreferredValue("framerateThresholdHigh", 40);
                if (framerateThresholdHigh)
                    this.hightFpsThreshold = framerateThresholdHigh;
            }
            else if (key === "measuringTime") {
                const measuringTime = preferencesService.getNumberPreferredValue("measuringTime", 1.5);
                if (measuringTime)
                    this.measurementPeriodInSeconds = measuringTime;
            }
            else if (key === "changeRate") {
                const changeRate = preferencesService.getNumberPreferredValue("changeRate", 0.05);
                if (changeRate)
                    this.changeRate = changeRate;
            }
            return true;
        }));
        window.addEventListener("beforeunload", () => {
            const resolutionFactor = 1 / renderer.getPixelRatio();
            preferencesService.updatePreferences([{ key: "resolutionFactor", value: resolutionFactor }]);
        });
    }
}
